import { AlertCircle, Loader2, Send } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';
import { Spinner } from '@/ui/Spinner';

import {
  formatarCnpj,
  formatarDataHora,
  ROTULO_DO_CAMPO,
  type CampoDeCorrecao,
  type DetalheDoVeiculo,
  type RespostaDeCorrecao,
} from './AcompanhamentoContrato';
import { useOpcoesDeCorrecao, useResponderCorrecao } from './AcompanhamentoDados';

/**
 * A resposta à devolução para correção (F06 × F07).
 *
 * SÓ APARECE O CAMPO QUE A TRIAGEM PEDIU (regra 7 de interface): contatos,
 * escolas, cooperativa — e a resposta por escrito, que é sempre obrigatória.
 * DRE e modalidade nunca são editáveis: mudariam o estrato do sorteio. A API
 * confere tudo de novo (campo pedido, escola da DRE, prazo, versão).
 *
 * Enviar grava de verdade: o veículo volta para a análise (REENVIADO) e a tela
 * troca pelo detalhe que a API devolveu.
 */

const NENHUMA = 'NENHUMA';
const SEM_COOPERATIVA = 'SEM_COOPERATIVA';
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Erros = Partial<Record<'email' | 'telefone' | 'escolas' | 'observacao', string>>;

function novaChave(): string {
  return crypto.randomUUID();
}

export function FormularioDeCorrecao({ veiculo }: { veiculo: DetalheDoVeiculo }) {
  const correcao = veiculo.correcao!;
  const pede = (campo: CampoDeCorrecao) => correcao.campos.includes(campo);
  const precisaDeOpcoes = pede('ESCOLAS') || pede('COOPERATIVA');
  const opcoes = useOpcoesDeCorrecao(veiculo.protocolo, precisaDeOpcoes);
  const enviar = useResponderCorrecao(veiculo.protocolo);

  const [email, setEmail] = useState(veiculo.contatos.email ?? '');
  const [telefone, setTelefone] = useState(veiculo.contatos.telefone ?? '');
  const [escolas, setEscolas] = useState<string[]>(() => {
    const atuais = veiculo.escolas.map((e) => e.codigoEol);
    return Array.from({ length: veiculo.maxEscolas }, (_, i) => atuais[i] ?? NENHUMA);
  });
  const [cooperativa, setCooperativa] = useState(veiculo.cooperativa?.cnpj ?? SEM_COOPERATIVA);
  const [observacao, setObservacao] = useState('');
  const [erros, setErros] = useState<Erros>({});
  // Uma chave por tentativa de envio: repetir o clique não grava duas vezes.
  const [chave, setChave] = useState(novaChave);

  const escolasDisponiveis = useMemo(() => opcoes.data?.escolas ?? [], [opcoes.data]);

  function validar(): Erros {
    const e: Erros = {};
    if (pede('CONTATOS')) {
      if (!EMAIL.test(email.trim())) e.email = 'Informe um e-mail válido.';
      if (!/^\d{10,11}$/.test(telefone.replace(/\D/g, ''))) {
        e.telefone = 'Informe o telefone com DDD, só números (10 ou 11 dígitos).';
      }
    }
    if (pede('ESCOLAS')) {
      const escolhidas = escolas.filter((c) => c !== NENHUMA);
      if (escolhidas.length === 0) e.escolas = 'Escolha ao menos a 1ª opção de escola.';
      else if (new Set(escolhidas).size !== escolhidas.length) {
        e.escolas = 'A mesma escola não pode aparecer duas vezes.';
      } else if (escolas[0] === NENHUMA) e.escolas = 'Comece pela 1ª opção de escola.';
    }
    const texto = observacao.trim();
    if (texto.length < 10)
      e.observacao = 'Escreva a sua resposta ao DTP, com ao menos 10 caracteres.';
    if (texto.length > 2000) e.observacao = 'A resposta pode ter no máximo 2000 caracteres.';
    return e;
  }

  function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    const encontrados = validar();
    setErros(encontrados);
    if (Object.keys(encontrados).length > 0) return;

    const resposta: RespostaDeCorrecao = {
      versao: veiculo.versao,
      chaveIdempotencia: chave,
      observacao: observacao.trim(),
    };
    if (pede('CONTATOS')) {
      resposta.contatos = { email: email.trim(), telefone: telefone.replace(/\D/g, '') };
    }
    if (pede('ESCOLAS')) resposta.escolas = escolas.filter((c) => c !== NENHUMA);
    if (pede('COOPERATIVA')) {
      resposta.cooperativaCnpj = cooperativa === SEM_COOPERATIVA ? null : cooperativa;
    }

    enviar.mutate(resposta, {
      onSuccess: () => {
        toast.success('Correção enviada', {
          description: 'O DTP vai analisar de novo a inscrição deste veículo.',
        });
      },
      onError: () => setChave(novaChave()),
    });
  }

  if (!correcao.podeResponder) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" aria-hidden />
        <AlertTitle>O prazo para corrigir terminou</AlertTitle>
        <AlertDescription>
          O prazo era {formatarDataHora(correcao.prazo)}. A inscrição deste veículo não pode mais
          ser corrigida por aqui; procure o DTP se precisar.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="border-warning/40 rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Responder à correção</CardTitle>
        <CardDescription>
          Corrija o que foi pedido e escreva a sua resposta. Prazo:{' '}
          <strong>{formatarDataHora(correcao.prazo)}</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={aoEnviar}
          noValidate
          aria-label="Responder à correção"
        >
          {pede('CONTATOS') && (
            <fieldset className="space-y-3">
              <legend className="text-sm font-semibold">{ROTULO_DO_CAMPO.CONTATOS}</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="correcao-email">E-mail</Label>
                  <Input
                    id="correcao-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={erros.email ? true : undefined}
                    aria-describedby={erros.email ? 'correcao-email-erro' : undefined}
                    className="h-11 sm:h-10"
                  />
                  {erros.email && (
                    <p id="correcao-email-erro" className="text-destructive text-xs">
                      {erros.email}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="correcao-telefone">Telefone com DDD</Label>
                  <Input
                    id="correcao-telefone"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    aria-invalid={erros.telefone ? true : undefined}
                    aria-describedby={erros.telefone ? 'correcao-telefone-erro' : undefined}
                    className="h-11 sm:h-10"
                  />
                  {erros.telefone && (
                    <p id="correcao-telefone-erro" className="text-destructive text-xs">
                      {erros.telefone}
                    </p>
                  )}
                </div>
              </div>
            </fieldset>
          )}

          {precisaDeOpcoes && opcoes.isPending && <Spinner rotulo="Carregando as opções…" />}
          {precisaDeOpcoes && opcoes.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" aria-hidden />
              <AlertTitle>Não foi possível carregar as opções</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>A lista de escolas ou de cooperativas está fora do ar. Tente de novo.</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void opcoes.refetch()}
                >
                  Tentar de novo
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {pede('ESCOLAS') && opcoes.isSuccess && (
            <fieldset
              className="space-y-3"
              aria-describedby={erros.escolas ? 'correcao-escolas-erro' : undefined}
            >
              <legend className="text-sm font-semibold">{ROTULO_DO_CAMPO.ESCOLAS}</legend>
              <p className="text-muted-foreground text-xs">
                Até {veiculo.maxEscolas}, em ordem de preferência, da mesma DRE e modalidade do
                veículo.
              </p>
              {escolas.map((valor, indice) => (
                <div key={indice} className="space-y-1.5">
                  <Label htmlFor={`correcao-escola-${indice}`}>{indice + 1}ª opção</Label>
                  <Select
                    value={valor}
                    onValueChange={(novo) =>
                      setEscolas((atual) => atual.map((c, i) => (i === indice ? novo : c)))
                    }
                  >
                    <SelectTrigger
                      id={`correcao-escola-${indice}`}
                      aria-label={`${indice + 1}ª opção de escola`}
                      className="h-11 sm:h-10"
                    >
                      <SelectValue placeholder="Escolha a escola" />
                    </SelectTrigger>
                    <SelectContent>
                      {indice > 0 && <SelectItem value={NENHUMA}>Nenhuma</SelectItem>}
                      {escolasDisponiveis.map((e) => (
                        <SelectItem key={e.codigoEol} value={e.codigoEol}>
                          {e.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {erros.escolas && (
                <p id="correcao-escolas-erro" className="text-destructive text-xs">
                  {erros.escolas}
                </p>
              )}
            </fieldset>
          )}

          {pede('COOPERATIVA') && opcoes.isSuccess && (
            <fieldset className="space-y-1.5">
              <legend className="text-sm font-semibold">{ROTULO_DO_CAMPO.COOPERATIVA}</legend>
              <Label htmlFor="correcao-cooperativa" className="sr-only">
                Cooperativa
              </Label>
              <Select value={cooperativa} onValueChange={setCooperativa}>
                <SelectTrigger
                  id="correcao-cooperativa"
                  aria-label="Cooperativa indicada"
                  className="h-11 sm:h-10"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_COOPERATIVA}>Sem cooperativa</SelectItem>
                  {(opcoes.data?.cooperativas ?? []).map((c) => (
                    <SelectItem key={c.cnpj} value={c.cnpj}>
                      {c.nome} · {formatarCnpj(c.cnpj)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </fieldset>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="correcao-observacao">Sua resposta ao DTP</Label>
            <Textarea
              id="correcao-observacao"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Conte o que você corrigiu ou esclareça o que foi pedido."
              maxLength={2000}
              className="min-h-24"
              aria-invalid={erros.observacao ? true : undefined}
              aria-describedby={
                erros.observacao ? 'correcao-observacao-erro' : 'correcao-observacao-ajuda'
              }
            />
            {erros.observacao ? (
              <p id="correcao-observacao-erro" className="text-destructive text-xs">
                {erros.observacao}
              </p>
            ) : (
              <p id="correcao-observacao-ajuda" className="text-muted-foreground text-xs">
                Obrigatória. A equipe do DTP lê esta resposta junto com a correção.
              </p>
            )}
          </div>

          {enviar.isError && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" aria-hidden />
              <AlertTitle>A correção não foi enviada</AlertTitle>
              <AlertDescription>{enviar.error.aviso}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="h-11 w-full sm:h-10 sm:w-auto"
            disabled={enviar.isPending || (precisaDeOpcoes && !opcoes.isSuccess)}
          >
            {enviar.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
            Enviar correção
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
